import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { extractErrorMessage } from "@/utils";

import { normalizeListResponse } from "../normalizeListResponse";
import {
  type TColumnDef,
  type TDataGridQuery,
  type TDataSource,
  type TFilterDef,
  type TFilterValue,
  type TSortOrder,
} from "../types";
import { clientSearch, clientSort, filtersToParams } from "../utils";

const SEARCH_DEBOUNCE_MS = 300;

type TSortState = { columnId: string; order: TSortOrder } | null;

export type TUseDataGridOptions<TRow> = {
  data: TDataSource<TRow>;
  columns: TColumnDef<TRow>[];
  filters: TFilterDef[];
  pageSize: number;
};

export type TUseDataGridResult<TRow> = {
  rows: TRow[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  // search
  searchInput: string;
  setSearchInput: (value: string) => void;
  // sort
  sort: TSortState;
  toggleSort: (columnId: string) => void;
  // filters
  filterValues: Record<string, TFilterValue>;
  setFilterValue: (id: string, value: TFilterValue) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  // pagination
  page: number;
  pageCount: number;
  offset: number;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

/**
 * Core grid state machine. Owns debounced search, sort, filters, pagination,
 * and the fetch lifecycle (with request cancellation). In server mode it sends
 * manual query params to the fetcher and normalizes the response; in client
 * mode it fetches the full list once and does search/sort/paginate in-memory.
 */
export function useDataGrid<TRow>({
  data,
  columns,
  filters,
  pageSize,
}: TUseDataGridOptions<TRow>): TUseDataGridResult<TRow> {
  const isServer = data.mode === "server";

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState<TSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, TFilterValue>>({});
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<TRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Full list for client mode (fetched once per deps change).
  const [clientRows, setClientRows] = useState<TRow[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Debounce search input (300ms) and reset pagination when it changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to the first page when the page size changes.
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const columnById = useMemo(() => {
    const map = new Map<string, TColumnDef<TRow>>();
    for (const column of columns) map.set(column.id, column);
    return map;
  }, [columns]);

  const sortColumn = sort ? columnById.get(sort.columnId) : undefined;
  const sortBy = sortColumn ? (sortColumn.sortKey ?? sortColumn.id) : undefined;

  const filterParams = useMemo(() => filtersToParams(filters, filterValues), [filters, filterValues]);

  const deps = data.deps ?? [];
  // Stable serialization of query-affecting inputs for the effect dep list.
  const queryKey = JSON.stringify({
    search: debouncedSearch,
    sortBy: isServer ? sortBy : null,
    order: isServer ? sort?.order : null,
    filters: isServer ? filterParams : null,
    page: isServer ? page : null,
    pageSize,
    mode: data.mode,
    tick: refetchTick,
  });

  const buildQuery = useCallback(
    (signal: AbortSignal): TDataGridQuery => {
      const limit = pageSize;
      const offset = (page - 1) * pageSize;
      return {
        search: debouncedSearch,
        sort_by: sortBy,
        sort_order: sort?.order,
        filters: filterParams,
        limit,
        offset,
        page,
        per_page: pageSize,
        signal,
      };
    },
    [debouncedSearch, sortBy, sort?.order, filterParams, page, pageSize]
  );

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const raw = await data.fetcher(buildQuery(ctrl.signal));
        if (cancelled || ctrl.signal.aborted) return;
        const normalized = normalizeListResponse<TRow>(raw);
        if (isServer) {
          setRows(normalized.rows);
          setTotal(normalized.total);
        } else {
          setClientRows(normalized.rows);
        }
      } catch (err) {
        if (cancelled || ctrl.signal.aborted) return;
        // Ignore abort errors from cancelled in-flight requests.
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(await extractErrorMessage(err));
        if (isServer) {
          setRows([]);
          setTotal(0);
        } else {
          setClientRows([]);
        }
      } finally {
        if (!cancelled && !ctrl.signal.aborted) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // buildQuery is intentionally excluded; queryKey captures its inputs plus deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, ...deps]);

  // Client mode: derive rows via in-memory search/sort/paginate.
  const clientProcessed = useMemo(() => {
    if (isServer) return null;
    const searchFields = data.mode === "client" ? data.searchFields : undefined;
    let result = clientSearch(clientRows, debouncedSearch, searchFields);
    if (sort) result = clientSort(result, columnById.get(sort.columnId), sort.order);
    const filteredTotal = result.length;
    const start = (page - 1) * pageSize;
    const paged = result.slice(start, start + pageSize);
    return { rows: paged, total: filteredTotal };
  }, [isServer, clientRows, debouncedSearch, sort, page, pageSize, columnById, data]);

  const effectiveRows = isServer ? rows : (clientProcessed?.rows ?? []);
  const effectiveTotal = isServer ? total : (clientProcessed?.total ?? 0);

  const pageCount = Math.max(1, Math.ceil(effectiveTotal / pageSize));

  // Clamp page if the total shrank below the current page.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const toggleSort = useCallback((columnId: string) => {
    setSort((prev) => {
      if (prev?.columnId === columnId) {
        return { columnId, order: prev.order === "asc" ? "desc" : "asc" };
      }
      return { columnId, order: "asc" };
    });
    setPage(1);
  }, []);

  const setFilterValue = useCallback((id: string, value: TFilterValue) => {
    setFilterValues((prev) => ({ ...prev, [id]: value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilterValues({});
    setPage(1);
  }, []);

  const activeFilterCount = Object.keys(filterParams).length;

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  const goToPage = useCallback((next: number) => setPage(next), []);
  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);

  return {
    rows: effectiveRows,
    total: effectiveTotal,
    loading,
    error,
    refetch,
    searchInput,
    setSearchInput,
    sort,
    toggleSort,
    filterValues,
    setFilterValue,
    clearFilters,
    activeFilterCount,
    page,
    pageCount,
    offset: (page - 1) * pageSize,
    goToPage,
    nextPage,
    prevPage,
  };
}
